import type { ReactNode } from 'react';
import { makeStyles } from '@mui/styles';
import { Card } from '@mui/material';
import { Link } from 'react-router-dom';

import Box from '@classify/react-core-components/Box';
import Button from '@classify/react-core-components/Button';
import Typography from '@classify/react-core-components/Typography';
import Narrative from '@classify/react-marketing-components/Narrative';

import { ROUTES } from '@openclassrooms/routing';
import { ImageCdn } from '@openclassrooms/images-cdn';
import browser from '@openclassrooms/utils/browser';
import { translate } from '@openclassrooms/translations';

const useStyles = makeStyles(({ spacing, breakpoints }) => ({
  imageWrapper: {
    display: 'none',

    [breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  image: {
    maxWidth: '100%',
    width: 186,
  },
  description: {
    padding: spacing(2, 0, 4, 0),
  },
  buttonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [breakpoints.up('sm')]: {
      display: 'initial',
    },
  },
}));

export interface AnonymousLoginBannerProps {
  title: string;
  description: string;
  image: ReactNode;
}

const AnonymousLoginBanner = ({ title, description, image }: AnonymousLoginBannerProps) => {
  const classes = useStyles();
  const targetPathQueryParam = new URLSearchParams({
    _target_path: browser.window.location.pathname,
    context: 'courseAccess',
  });

  return (
    <Narrative
      sx={{
        padding: (theme) => theme.spacing(5),
        boxShadow: (theme) => theme.shadows.banner,
      }}
      component={Card}
      media={
        <Box className={classes.imageWrapper}>
          <ImageCdn className={classes.image} src={image} width={315} alt="" />
        </Box>
      }
    >
      <Typography variant="h4" color="brand.secondary">
        {title}
      </Typography>

      <Typography variant="subtitle1" className={classes.description}>
        {description}
      </Typography>

      <div className={classes.buttonWrapper}>
        <Button
          component={Link}
          to={`${ROUTES.AUTH.LOGIN}?${targetPathQueryParam}`}
          variant="primary"
          sx={{
            width: 'max-content',
            marginBottom: { xs: 1, sm: 0 },
            marginRight: { xs: 0, sm: 1 },
          }}
        >
          {translate('quizpage.not_logged.login_banner.cta.create_account')}
        </Button>
        <Button
          component={Link}
          to={`${ROUTES.AUTH.LOGIN}?${targetPathQueryParam}`}
          variant="tertiary"
        >
          {translate('quizpage.not_logged.login_banner.cta.login')}
        </Button>
      </div>
    </Narrative>
  );
};

export default AnonymousLoginBanner;
